import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ReservationDateTimeSelectorV2.css";
import ReservationSingleDayCalendarModal from "./ReservationSingleDayCalendarModal";
import {
    MS_IN_HOUR,
    MS_IN_MINUTE,
    alignMinuteToStep,
    calculateDayExtensionHours,
    cloneDate,
    formatDate,
    formatDateTime,
    formatPopupDateTime,
    formatPopupDuration,
    formatTime,
    getMinuteOptions,
    parseDateLike,
    roundedDateWithInterval,
    setTimePortion,
} from "./ReservationDateTimeSelectorUtils";

const WEBSITE_WARNING_OFFICE_OPEN_MINUTES = 10 * 60;
const WEBSITE_WARNING_OFFICE_CLOSE_MINUTES = 17 * 60;

const getMinutesOfDay = (date) => date.getHours() * 60 + date.getMinutes();

const isOutsideWebsiteWarningOfficeHours = (date) => {
    const minutes = getMinutesOfDay(date);
    return minutes < WEBSITE_WARNING_OFFICE_OPEN_MINUTES || minutes > WEBSITE_WARNING_OFFICE_CLOSE_MINUTES;
};

const buildInitialPeriodHourly = ({
    initialStart,
    initialEnd,
    stepMinutes,
    minimumDurationMinutes,
}) => {
    const parsedStart = parseDateLike(initialStart);
    const minimumStart = roundedDateWithInterval(new Date(), stepMinutes);
    const minimumDurationMs = Math.max(1, Math.round(minimumDurationMinutes)) * MS_IN_MINUTE;
    const parsedEnd = parseDateLike(initialEnd);

    const start = parsedStart ? cloneDate(parsedStart) : cloneDate(minimumStart);
    const fallbackEnd = new Date(start.getTime() + minimumDurationMs);
    const end = parsedEnd && parsedEnd.getTime() - start.getTime() >= minimumDurationMs
        ? cloneDate(parsedEnd)
        : fallbackEnd;

    return { minimumStart, start, end };
};

export default function ReservationDateTimeSelectorHourly({
    startTimeStepMinutes = 30,
    minimumDurationMinutes = 60,
    initialStart = null,
    initialEnd = null,
    locationSchedule = [],
    locationWebsite = "",
    useCurrentTimeDefault = false,
    onPeriodChange,
    children = null,
}) {
    const [isStartDateModalOpen, setIsStartDateModalOpen] = useState(false);
    const [isStartTimeModalOpen, setIsStartTimeModalOpen] = useState(false);
    const [isEndModalOpen, setIsEndModalOpen] = useState(false);
    const [isAutoCorrectionModalOpen, setIsAutoCorrectionModalOpen] = useState(false);
    const [isEndConfirmBlinking, setIsEndConfirmBlinking] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const previousTempEndRef = useRef(null);

    const minimumDurationMs = Math.max(1, Math.round(minimumDurationMinutes)) * MS_IN_MINUTE;
    const resolvedInitialPeriod = useMemo(
        () => buildInitialPeriodHourly({
            initialStart,
            initialEnd,
            stepMinutes: startTimeStepMinutes,
            minimumDurationMinutes,
        }),
        [
            initialStart,
            initialEnd,
            startTimeStepMinutes,
            minimumDurationMinutes,
        ]
    );

    const [minimumStart, setMinimumStart] = useState(() => cloneDate(resolvedInitialPeriod.minimumStart));
    const [start, setStart] = useState(() => cloneDate(resolvedInitialPeriod.start));
    const [end, setEnd] = useState(() => cloneDate(resolvedInitialPeriod.end));
    const [tempStart, setTempStart] = useState(() => cloneDate(resolvedInitialPeriod.start));
    const [tempEnd, setTempEnd] = useState(() => cloneDate(resolvedInitialPeriod.end));

    const invalidStart = start.getTime() < minimumStart.getTime();
    const invalidEnd = end.getTime() - start.getTime() < minimumDurationMs;

    const startMinuteOptions = useMemo(
        () => getMinuteOptions(startTimeStepMinutes),
        [startTimeStepMinutes]
    );

    const getNearestValidStart = (candidateStart) => {
        if (candidateStart.getTime() < minimumStart.getTime()) {
            return cloneDate(minimumStart);
        }
        return candidateStart;
    };

    const applyStartChange = (newStartValue) => {
        const requestedStart = cloneDate(newStartValue);
        const nextStart = getNearestValidStart(requestedStart);
        const wasStartAutoAdjusted = requestedStart.getTime() !== nextStart.getTime();
        const delta = nextStart.getTime() - start.getTime();
        const nextEnd = new Date(end.getTime() + delta);

        setStart(nextStart);
        setEnd(nextEnd);
        setSnackbarMessage(
            wasStartAutoAdjusted
                ? "Start date/time adjusted automatically"
                : "Return date/time adjusted automatically"
        );
    };

    const applyPeriodReset = () => {
        const nextMinimumStart = roundedDateWithInterval(new Date(), startTimeStepMinutes);
        const nextStart = cloneDate(nextMinimumStart);
        const nextEnd = new Date(nextStart.getTime() + minimumDurationMs);

        setMinimumStart(nextMinimumStart);
        setStart(nextStart);
        setEnd(nextEnd);
        setTempStart(nextStart);
        setTempEnd(nextEnd);
    };

    useEffect(() => {
        setMinimumStart(cloneDate(resolvedInitialPeriod.minimumStart));
        setStart(cloneDate(resolvedInitialPeriod.start));
        setEnd(cloneDate(resolvedInitialPeriod.end));
        setTempStart(cloneDate(resolvedInitialPeriod.start));
        setTempEnd(cloneDate(resolvedInitialPeriod.end));
    }, [resolvedInitialPeriod]);

    useEffect(() => {
        if (typeof onPeriodChange === "function") {
            onPeriodChange({ start, end });
        }
    }, [start, end, onPeriodChange]);

    useEffect(() => {
        const tick = () => {
            const nextMinimumStart = roundedDateWithInterval(new Date(), startTimeStepMinutes);
            setMinimumStart(nextMinimumStart);

            if (start.getTime() < nextMinimumStart.getTime()) {
                setIsAutoCorrectionModalOpen(true);
            }
        };

        tick();
        const intervalId = window.setInterval(tick, 1000);
        return () => window.clearInterval(intervalId);
    }, [start, startTimeStepMinutes]);

    useEffect(() => {
        if (!snackbarMessage) return undefined;
        const timeoutId = window.setTimeout(() => {
            setSnackbarMessage("");
        }, 2800);
        return () => window.clearTimeout(timeoutId);
    }, [snackbarMessage]);

    useEffect(() => {
        if (!isEndModalOpen) {
            setIsEndConfirmBlinking(false);
            previousTempEndRef.current = null;
            return;
        }

        const currentTempEnd = tempEnd.getTime();
        if (previousTempEndRef.current === null) {
            previousTempEndRef.current = currentTempEnd;
            return;
        }

        if (currentTempEnd === previousTempEndRef.current) return;

        previousTempEndRef.current = currentTempEnd;
        setIsEndConfirmBlinking(false);

        const frameId = window.requestAnimationFrame(() => {
            setIsEndConfirmBlinking(true);
        });
        const timeoutId = window.setTimeout(() => {
            setIsEndConfirmBlinking(false);
        }, 1400);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.clearTimeout(timeoutId);
        };
    }, [isEndModalOpen, tempEnd]);

    const handleStartDateOpen = () => {
        setTempStart(cloneDate(start));
        setIsStartDateModalOpen(true);
    };

    const handleStartTimeOpen = () => {
        const normalized = roundedDateWithInterval(start, startTimeStepMinutes);
        setTempStart(normalized);
        setIsStartDateModalOpen(false);
        setIsStartTimeModalOpen(true);
    };

    const handleEndModalOpen = () => {
        setIsStartDateModalOpen(false);
        setTempEnd(cloneDate(end));
        setIsEndModalOpen(true);
    };

    const handleStartDateConfirm = (selectedDate) => {
        const nextWithSelectedDay = setTimePortion(
            selectedDate,
            tempStart.getHours(),
            tempStart.getMinutes()
        );
        const nextStart = getNearestValidStart(nextWithSelectedDay);
        setTempStart(nextStart);
        applyStartChange(nextStart);
        setIsStartDateModalOpen(false);
    };

    const handleStartTimeConfirm = () => {
        applyStartChange(tempStart);
        setIsStartTimeModalOpen(false);
    };

    const handleStartTimeCancel = () => {
        setTempStart(cloneDate(start));
        setIsStartTimeModalOpen(false);
    };

    const handleEndModalCancel = () => {
        setTempEnd(cloneDate(end));
        setIsEndModalOpen(false);
    };

    const handleEndConfirm = () => {
        setEnd(cloneDate(tempEnd));
        setIsEndModalOpen(false);
    };

    const handleAutoCorrectionConfirm = () => {
        const nextMinimumStart = roundedDateWithInterval(new Date(), startTimeStepMinutes);
        setMinimumStart(nextMinimumStart);
        const duration = end.getTime() - start.getTime();
        const nextStart = cloneDate(nextMinimumStart);
        const nextEnd = new Date(nextStart.getTime() + duration);
        setStart(nextStart);
        setEnd(nextEnd);
        setSnackbarMessage("Return date/time adjusted automatically");
        setIsAutoCorrectionModalOpen(false);
    };

    const minimumEnd = useMemo(() => {
        const roundedStart = roundedDateWithInterval(start, startTimeStepMinutes);
        return new Date(roundedStart.getTime() + minimumDurationMs);
    }, [start, startTimeStepMinutes, minimumDurationMs]);

    const tempEndDuration = tempEnd.getTime() - start.getTime();
    const isTempStartUnchanged = tempStart.getTime() === start.getTime();
    const isTempEndUnchanged = tempEnd.getTime() === end.getTime();
    const isTempStartInvalid = tempStart.getTime() < minimumStart.getTime();
    const isTempEndBelowMinimum = tempEnd.getTime() - start.getTime() < minimumDurationMs;
    const hasLocationWebsite = Boolean(locationWebsite && String(locationWebsite).trim());
    const showWebsiteOpeningWarning = Boolean(
        hasLocationWebsite &&
        useCurrentTimeDefault &&
        (isOutsideWebsiteWarningOfficeHours(start) || isOutsideWebsiteWarningOfficeHours(end))
    );

    const shiftTempEndBy = (deltaMs) => {
        setTempEnd((previous) => new Date(previous.getTime() + deltaMs));
    };

    const handleAddDayToTempEnd = () => {
        const oneDayDurationMs = 24 * MS_IN_HOUR;
        const currentDurationMs = tempEnd.getTime() - start.getTime();

        if (currentDurationMs < oneDayDurationMs) {
            shiftTempEndBy(oneDayDurationMs - currentDurationMs);
            return;
        }

        const extensionHours = calculateDayExtensionHours(start, tempEnd);
        shiftTempEndBy(extensionHours * MS_IN_HOUR);
    };

    const endFieldTextClass = invalidEnd ? "kav-rdt2__value is-error" : "kav-rdt2__value";
    const startFieldTextClass = invalidStart ? "kav-rdt2__value is-error" : "kav-rdt2__value";

    return (
        <div className="kav-rdt2">
            <div className="kav-rdt2__header">
                {/*<h3>Date &amp; Time</h3>*/}
                {/*<button*/}
                {/*    type="button"*/}
                {/*    className="kav-rdt2__resetBtn"*/}
                {/*    onClick={applyPeriodReset}*/}
                {/*>*/}
                {/*    Reset*/}
                {/*</button>*/}
            </div>

            <div className="kav-rdt2__fromCalendarWrap">
                <div className="kav-rdt2__row">
                    <div className="kav-rdt2__label">From</div>
                    <div className="kav-rdt2__fromFields">
                        <button
                            type="button"
                            className="kav-rdt2__fieldBtn"
                            onClick={handleStartDateOpen}
                        >
                            <i className="bi bi-calendar-date kav-rdt2__icon" aria-hidden="true"></i>
                            <span className={startFieldTextClass}>{formatDate(start)}</span>
                        </button>

                        <button
                            type="button"
                            className="kav-rdt2__fieldBtn"
                            onClick={handleStartTimeOpen}
                        >
                            <i className="bi bi-clock kav-rdt2__icon" aria-hidden="true"></i>
                            <span className={startFieldTextClass}>{formatTime(start)}</span>
                        </button>
                    </div>
                </div>

                <ReservationSingleDayCalendarModal
                    isOpen={isStartDateModalOpen}
                    selectedDate={tempStart}
                    minDate={minimumStart}
                    locationSchedule={locationSchedule}
                    onRequestClose={() => setIsStartDateModalOpen(false)}
                    onConfirm={handleStartDateConfirm}
                />
            </div>

            <div className="kav-rdt2__row kav-rdt2__row--single">
                <div className="kav-rdt2__label">To</div>
                <button
                    type="button"
                    className="kav-rdt2__fieldBtn kav-rdt2__fieldBtn--wide"
                    onClick={handleEndModalOpen}
                >
                    <i className="bi bi-calendar-date kav-rdt2__icon" aria-hidden="true"></i>
                    <span className={endFieldTextClass}>{formatDate(end)}</span>
                    <i className="bi bi-clock kav-rdt2__icon kav-rdt2__icon--right" aria-hidden="true"></i>
                    <span className={endFieldTextClass}>{formatTime(end)}</span>
                </button>
            </div>

            <button
                type="button"
                className="kav-rdt2__changePeriodCta"
                onClick={handleEndModalOpen}
            >
                KLIK HIER OM UREN EN DAGEN TE VERANDEREN
            </button>

            {invalidStart ? (
                <p className="kav-rdt2__errorText">
                    Start is earlier than minimum allowed ({formatDateTime(minimumStart)}).
                </p>
            ) : null}

            {invalidEnd ? (
                <p className="kav-rdt2__errorText">
                    {`Minimum reservation duration is ${Math.round(minimumDurationMs / MS_IN_MINUTE)} minutes.`}
                </p>
            ) : null}

            {showWebsiteOpeningWarning ? (
                <div className="kav-rdt2__websiteNotice">
                    Controleer{" "}
                    <a
                        href={locationWebsite}
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        via deze link
                    </a>{" "}
                    de openingstijden van deze ophaallocatie.
                </div>
            ) : null}

            {isStartTimeModalOpen ? (
                <div className="kav-rdt2-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-rdt2-modal__backdrop"
                        onClick={handleStartTimeCancel}
                        aria-label="Close"
                    />
                    <div className="kav-rdt2-modal__card kav-rdt2-modal__card--startTime">
                        <h4>Pick-up time</h4>

                        <div className="kav-rdt2-timePickers">
                            <select
                                className="kav-rdt2-select"
                                value={String(tempStart.getHours()).padStart(2, "0")}
                                onChange={(event) => {
                                    const nextHour = Number(event.target.value);
                                    const nextMinute = alignMinuteToStep(tempStart.getMinutes(), startTimeStepMinutes);
                                    const nextStart = setTimePortion(tempStart, nextHour, nextMinute);
                                    setTempStart(getNearestValidStart(nextStart));
                                }}
                            >
                                {Array.from({ length: 24 }).map((_, hour) => (
                                    <option key={`start-hour-${hour}`} value={String(hour).padStart(2, "0")}>
                                        {String(hour).padStart(2, "0")}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="kav-rdt2-select"
                                value={String(alignMinuteToStep(tempStart.getMinutes(), startTimeStepMinutes)).padStart(2, "0")}
                                onChange={(event) => {
                                    const nextMinute = Number(event.target.value);
                                    const nextStart = setTimePortion(tempStart, tempStart.getHours(), nextMinute);
                                    setTempStart(getNearestValidStart(nextStart));
                                }}
                            >
                                {startMinuteOptions.map((minute) => (
                                    <option key={`start-minute-${minute}`} value={String(minute).padStart(2, "0")}>
                                        {String(minute).padStart(2, "0")}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="kav-rdt2-modal__actions">
                            <button type="button" className="kav-rdt2-btn kav-rdt2-btn--ghost" onClick={handleStartTimeCancel}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--primary"
                                onClick={handleStartTimeConfirm}
                                disabled={isTempStartUnchanged || isTempStartInvalid}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {isEndModalOpen ? (
                <div className="kav-rdt2-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-rdt2-modal__backdrop"
                        onClick={handleEndModalCancel}
                        aria-label="Close"
                    />
                    <div className="kav-rdt2-modal__card">
                        <button
                            type="button"
                            className="kav-rdt2-modal__close"
                            onClick={handleEndModalCancel}
                            aria-label="Close"
                        >
                            <i className="bi bi-x-lg" aria-hidden="true"></i>
                        </button>

                        <div className="kav-rdt2-endSummary">
                            <div className="kav-rdt2-endSummary__block">
                                <p className="kav-rdt2-endSummary__label">Pick-up time</p>
                                <p className="kav-rdt2-endSummary__value">{formatPopupDateTime(start)}</p>
                            </div>
                            <div className="kav-rdt2-endSummary__block">
                                <p className="kav-rdt2-endSummary__label">Return time</p>
                                <p className="kav-rdt2-endSummary__value">{formatPopupDateTime(tempEnd)}</p>
                            </div>
                            <p
                                className="kav-rdt2-endSummary__duration"
                                style={{ color: "var(--wl-primary)", fontWeight: 800 }}
                            >
                                {formatPopupDuration(tempEndDuration)}
                            </p>
                        </div>

                        <div className="kav-rdt2-quickActions">
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--ghost"
                                onClick={() => shiftTempEndBy(MS_IN_HOUR)}
                            >
                                +1 Hour
                            </button>
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--ghost"
                                onClick={handleAddDayToTempEnd}
                            >
                                +1 Day
                            </button>
                        </div>

                        {isTempEndBelowMinimum ? (
                            <p className="kav-rdt2__errorText">
                                End is earlier than minimum allowed duration.
                            </p>
                        ) : null}

                        <div className="kav-rdt2-modal__actions kav-rdt2-modal__actions--center">
                            <button
                                type="button"
                                className={`kav-rdt2-btn kav-rdt2-btn--confirmWide${isEndConfirmBlinking ? " kav-rdt2-btn--confirmWideBlink" : ""}`}
                                onClick={handleEndConfirm}
                                disabled={isTempEndUnchanged || isTempEndBelowMinimum}
                            >
                                Confirm {formatPopupDateTime(tempEnd)}
                            </button>
                        </div>

                        {tempEnd.getTime() > minimumEnd.getTime() ? (
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--ghost kav-rdt2-btn--resetInline"
                                onClick={() => setTempEnd(cloneDate(minimumEnd))}
                            >
                                Reset
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {isAutoCorrectionModalOpen ? (
                <div className="kav-rdt2-modal" role="dialog" aria-modal="true">
                    <div className="kav-rdt2-modal__card">
                        <h4>Vertrektijd wordt aangepast</h4>
                        <p>
                            De vertrektijd ligt in het verleden. Druk ‘OK’ om de eerstvolgende vertrektijd te
                            selecteren. De opgegeven reserveringstijd wordt aangehouden.
                        </p>
                        <div className="kav-rdt2-modal__actions kav-rdt2-modal__actions--single">
                            <button type="button" className="kav-rdt2-btn kav-rdt2-btn--primary" onClick={handleAutoCorrectionConfirm}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {snackbarMessage ? (
                <div className="kav-rdt2-snackbar" role="status" aria-live="polite">
                    {snackbarMessage}
                </div>
            ) : null}

            {children}
        </div>
    );
}
